/**
 * Field tooltip dictionary.
 *
 * Lookup key: the FieldDef.canonical_name as it appears in the schema JSON,
 * with the `[*]` wildcard segment stripped. So a tooltip keyed
 * "applicant.witnesses.given_names" matches the schema field
 * "applicant.witnesses[*].given_names" inside a repeater.
 *
 * Examples are synthetic — invented values that demonstrate the expected
 * format. Rules:
 *   - Names: plausible but not real-celebrity (no "Hugh Jackman").
 *   - Phone numbers: AU + international format with placeholder digits.
 *   - Addresses: real suburb format, invented street numbers/names.
 *   - Dates: ISO `YYYY-MM-DD`.
 *   - Spread examples across an Australian + foreign + edge case.
 *
 * If a key isn't found, `<FieldTooltip>` simply isn't rendered — fields
 * without entries continue to work unchanged.
 *
 * To add a new tooltip: drop a new entry below keyed by the field's
 * canonical_name (strip any `[*]`). See tasks/FIELD-TOOLTIPS.md.
 */

export interface FieldTooltipEntry {
  description: string;
  examples: string[];
}

export const FIELD_TOOLTIPS: Record<string, FieldTooltipEntry> = {
  // ---------------------------------------------------------------------------
  // Applicant — name
  // ---------------------------------------------------------------------------
  'applicant.name.family_name': {
    description:
      'Your last name (surname) exactly as it appears on your passport, including hyphens, accents, or apostrophes.',
    examples: ['Smith', 'García-López', "O'Brien"],
  },
  'applicant.name.given_names': {
    description:
      'All your first and middle names exactly as on your passport, in the same order.',
    examples: ['Sarah Jane', 'Michael Anthony Christopher', 'Anh Thi'],
  },
  'applicant.name.name_in_script': {
    description:
      'Your name in your native script (e.g. Chinese characters, Arabic, Cyrillic). Leave blank if your passport name is already in Latin script.',
    examples: ['สาราห์ สมิธ', '李明', 'Иван Петров'],
  },

  // ---------------------------------------------------------------------------
  // Applicant — identity
  // ---------------------------------------------------------------------------
  'applicant.dob': {
    description:
      'Your date of birth in YYYY-MM-DD format, exactly as on your passport.',
    examples: ['1992-03-15', '1985-11-02'],
  },
  'applicant.sex': {
    description:
      'Your sex as recorded on your passport. If your passport shows X, select "Indeterminate / unspecified".',
    examples: ['Female', 'Male'],
  },
  'applicant.place_of_birth.town': {
    description:
      'The city or town where you were born, as shown on your birth certificate.',
    examples: ['Sydney', 'Bangkok', 'London'],
  },
  'applicant.place_of_birth.state_province': {
    description:
      'The state, province, or region of your place of birth. Leave blank if not applicable.',
    examples: ['NSW', 'Bangkok Metropolitan Region', 'Greater London'],
  },
  'applicant.place_of_birth.country': {
    description:
      'The country where you were born — the country that exists today, even if borders or names have changed.',
    examples: ['Australia', 'Thailand', 'United Kingdom'],
  },
  'applicant.usual_country_of_residence': {
    description:
      'The country where you currently live and spend most of your time, regardless of citizenship.',
    examples: ['Australia', 'Thailand', 'United Kingdom'],
  },

  // ---------------------------------------------------------------------------
  // Applicant — citizenship
  // ---------------------------------------------------------------------------
  'applicant.citizenship.has_other_citizenship': {
    description:
      'Select Yes if you hold citizenship of more than one country (dual or multiple nationality).',
    examples: ['Yes — UK and Australian', 'No — only Thai'],
  },
  'applicant.citizenship.is_citizen_of_passport_country': {
    description:
      'Confirm that the country which issued your passport is also a country you are a citizen of. Almost always Yes.',
    examples: ['Yes'],
  },
  'applicant.citizenship.other_countries': {
    description:
      'List every other country (besides your main passport country) where you hold citizenship.',
    examples: ['United Kingdom', 'New Zealand, Canada'],
  },

  // ---------------------------------------------------------------------------
  // Applicant — passport
  // ---------------------------------------------------------------------------
  'applicant.passport.number': {
    description:
      'Your passport number exactly as printed. Format varies by country — use the document number, not the personal ID number.',
    examples: ['PA1234567', 'M12345678', '551234567'],
  },
  'applicant.passport.country': {
    description: 'The country that issued the passport.',
    examples: ['Australia', 'Thailand', 'United Kingdom'],
  },
  'applicant.passport.nationality': {
    description:
      'The nationality recorded on your passport, usually the same as the issuing country.',
    examples: ['Australian', 'Thai', 'British'],
  },
  'applicant.passport.date_of_issue': {
    description:
      'The date your passport was issued, in YYYY-MM-DD format. Found on the photo page.',
    examples: ['2020-06-12', '2018-11-30'],
  },
  'applicant.passport.date_of_expiry': {
    description:
      'The date your passport expires. If it expires within 6 months, contact us — you may need to renew before lodging.',
    examples: ['2030-06-11', '2028-11-29'],
  },
  'applicant.passport.place_of_issue': {
    description:
      'The city or office that issued your passport (printed on the photo page).',
    examples: ['Canberra', 'Bangkok', 'London'],
  },

  // ---------------------------------------------------------------------------
  // Applicant — contact
  // ---------------------------------------------------------------------------
  'applicant.email': {
    description:
      'A personal email you check regularly. The Department of Home Affairs and ADI will send important updates here.',
    examples: ['sarah.smith@gmail.com', 'm.tran92@outlook.com'],
  },
  'applicant.phone_mobile': {
    description:
      'Your mobile number including country code. Use a number you can answer — Home Affairs may call to verify identity.',
    examples: ['+61 412 345 678', '+66 81 234 5678', '+44 7700 900123'],
  },
  'applicant.phone_home': {
    description:
      'A landline number, with country code. Leave blank if you do not have a landline.',
    examples: ['+61 2 9000 0000', '+44 20 7946 0000'],
  },
  'applicant.phone_business': {
    description:
      'A daytime work number with country code. Leave blank if you do not want Home Affairs contacting you at work.',
    examples: ['+61 2 8000 1234', '+66 2 123 4567'],
  },
  'applicant.residential_address': {
    description:
      'The address where you currently live. Use a unit/street number; PO Boxes are not acceptable for residential address.',
    examples: ['Unit 4 / 12 Smith Street, Bondi NSW 2026, Australia'],
  },
  'applicant.postal_address': {
    description:
      'The address where you receive mail. PO Boxes are fine here.',
    examples: ['PO Box 1234, Bondi Junction NSW 1355, Australia'],
  },
  'applicant.postal_same_as_residential': {
    description:
      'Tick Yes if mail should be sent to the same address you live at.',
    examples: ['Yes'],
  },

  // ---------------------------------------------------------------------------
  // Applicant — relationship to sponsor
  // ---------------------------------------------------------------------------
  'applicant.relationship.status': {
    description:
      'Your current relationship to your sponsor: married, de facto (living together for 12+ months), or engaged (PMV only).',
    examples: ['Married', 'De facto', 'Engaged'],
  },
  'applicant.relationship.date_first_met': {
    description:
      'The date you and your sponsor first met in person. If you can only remember the month or year, give your best estimate.',
    examples: ['2019-03-08', '2017-12-25'],
  },
  'applicant.relationship.location_first_met': {
    description: 'Where you first met your sponsor in person.',
    examples: ['Sydney, Australia', 'Bangkok, Thailand', 'Online — first met in person Bali, 2019'],
  },
  'applicant.relationship.date_relationship_began': {
    description:
      'The date you both agreed to be a committed couple — exclusive, mutual relationship.',
    examples: ['2020-01-15', '2018-09-01'],
  },
  'applicant.relationship.date_de_facto_began': {
    description:
      'The date you began living together as a couple in a genuine, exclusive de facto relationship.',
    examples: ['2021-02-01', '2019-06-15'],
  },
  'applicant.relationship.date_committed_shared_life': {
    description:
      'The date you mutually committed to a shared life — moving in together, getting engaged, joint finances, or similar milestone.',
    examples: ['2021-02-01', '2020-12-24'],
  },
  'applicant.relationship.date_of_marriage': {
    description:
      'Your marriage date as on your marriage certificate, in YYYY-MM-DD format.',
    examples: ['2023-04-22', '2022-10-08'],
  },
  'applicant.relationship.place_of_marriage': {
    description: 'The city, state, and country where you were married.',
    examples: ['Sydney, NSW, Australia', 'Phuket, Thailand'],
  },
  'applicant.relationship.celebrant_name': {
    description:
      'Full name of the person who married you (celebrant, registrar, or religious officiant).',
    examples: ['Jane Wilson', 'Pastor David Lee'],
  },
  'applicant.relationship.have_met_in_person': {
    description:
      'Confirm that you and your sponsor have physically met as adults. This is a strict requirement.',
    examples: ['Yes'],
  },
  'applicant.relationship.lived_separately': {
    description:
      'Have there been any periods you lived apart since the relationship began? Include reason and dates if Yes.',
    examples: ['No', 'Yes — Jan 2022 to Aug 2022, sponsor working in UK'],
  },
  'applicant.relationship.financial_aspects': {
    description:
      'Describe how you share finances: joint accounts, shared bills, joint loans, who pays what. The Department wants evidence of pooled financial commitment.',
    examples: [
      'We have a joint Westpac savings account opened in March 2022. We split rent and bills equally and contribute to a joint home deposit.',
    ],
  },
  'applicant.relationship.nature_of_household': {
    description:
      'Describe how you run a household together: who cooks, cleans, manages bills, shares chores.',
    examples: [
      'We share a 2-bedroom apartment in Bondi. We split cooking and grocery shopping; I manage the bills and Marco handles maintenance.',
    ],
  },
  'applicant.relationship.social_aspects': {
    description:
      'Describe how others see you as a couple: shared friends, family events, social media, joint invitations.',
    examples: [
      "Our families have met multiple times. We attend each other's family birthdays and holidays. Friends invite us as a couple.",
    ],
  },
  'applicant.relationship.nature_of_commitment': {
    description:
      'Describe your long-term commitment: future plans, children, property, mutual support.',
    examples: [
      'We plan to buy a home together in 2027 and start a family within 3 years. We support each other through career changes and family illness.',
    ],
  },

  // ---------------------------------------------------------------------------
  // Sponsor
  // ---------------------------------------------------------------------------
  'sponsor.name.family_name': {
    description: "Sponsor's last name as it appears on their passport or Australian ID.",
    examples: ['Johnson', 'Nguyen', 'Papadopoulos'],
  },
  'sponsor.name.given_names': {
    description: "Sponsor's first and middle names as on their passport or Australian ID.",
    examples: ['David James', 'Linh Mai'],
  },
  'sponsor.dob': {
    description: "Sponsor's date of birth in YYYY-MM-DD format.",
    examples: ['1988-07-22', '1979-12-04'],
  },
  'sponsor.email': {
    description:
      "Sponsor's personal email — used for sponsor approval and communications.",
    examples: ['david.johnson@gmail.com'],
  },
  'sponsor.phone_mobile': {
    description: "Sponsor's mobile number with country code.",
    examples: ['+61 412 987 654'],
  },
  'sponsor.au_citizenship.by_birth': {
    description:
      'Tick Yes if the sponsor was born in Australia and is therefore a citizen by birth.',
    examples: ['Yes', 'No'],
  },
  'sponsor.au_citizenship.by_grant': {
    description:
      'Tick Yes if the sponsor became Australian by being granted citizenship (citizenship ceremony / certificate).',
    examples: ['Yes', 'No'],
  },
  'sponsor.au_citizenship.date_of_arrival': {
    description: 'Date the sponsor first arrived in Australia, in YYYY-MM-DD format.',
    examples: ['2010-08-15'],
  },
  'sponsor.citizenship.has_other_citizenship': {
    description:
      'Does the sponsor hold citizenship of any country other than Australia?',
    examples: ['Yes — also British', 'No'],
  },

  // ---------------------------------------------------------------------------
  // Family — children, immediate, migrating
  // ---------------------------------------------------------------------------
  'applicant.children.family_name': {
    description: "Child's last name as on their birth certificate or passport.",
    examples: ['Smith', 'Tran-Smith'],
  },
  'applicant.children.given_names': {
    description: "Child's first and middle names as on their birth certificate or passport.",
    examples: ['Emily Rose', 'Liam James'],
  },
  'applicant.children.dob': {
    description: "Child's date of birth in YYYY-MM-DD format.",
    examples: ['2019-09-12'],
  },
  'applicant.immediate_family.entries.family_name': {
    description: "Family member's last name (parent, sibling, etc.).",
    examples: ['Smith', 'Garcia'],
  },
  'applicant.immediate_family.entries.given_names': {
    description: "Family member's first and middle names.",
    examples: ['Margaret Anne', 'Carlos Eduardo'],
  },
  'applicant.immediate_family.entries.relationship_to_applicant': {
    description: 'How this person is related to you (the applicant).',
    examples: ['Mother', 'Father', 'Brother', 'Sister'],
  },
  'applicant.immediate_family.entries.country_of_residence': {
    description: 'Country where this family member currently lives.',
    examples: ['Australia', 'Thailand', 'United Kingdom'],
  },
  'applicant.immediate_family.entries.dob': {
    description: "Family member's date of birth in YYYY-MM-DD. If unknown, use approximate year (e.g. 1965-01-01).",
    examples: ['1965-04-18', '1972-11-30'],
  },

  // ---------------------------------------------------------------------------
  // Other names / passports / IDs
  // ---------------------------------------------------------------------------
  'applicant.other_names.has_other_names': {
    description:
      'Have you ever been known by another name? Includes maiden name, names from previous marriages, name changes, aliases.',
    examples: ['Yes — maiden name was Brown', 'No'],
  },
  'applicant.other_names.entries.family_name': {
    description: 'A previous last name you used (e.g. maiden name).',
    examples: ['Brown', 'Lee'],
  },
  'applicant.other_names.entries.given_names': {
    description: 'Previous first and middle names you used.',
    examples: ['Sarah Jane', 'Anh Thi'],
  },
  'applicant.other_names.entries.reason': {
    description: 'Why you used this other name.',
    examples: ['Maiden name', 'Married name', 'Deed poll'],
  },
  'applicant.national_id.identification_number': {
    description:
      'National ID number for the country of issue. Format varies by country.',
    examples: ['1100200345678', '012-3456789-1'],
  },
  'applicant.national_id.country_of_issue': {
    description: 'The country that issued this national ID.',
    examples: ['Thailand', 'Indonesia'],
  },

  // ---------------------------------------------------------------------------
  // Visa / character / health
  // ---------------------------------------------------------------------------
  'applicant.visa_history.previously_applied': {
    description:
      'Have you ever applied for any Australian visa before? Include all subclasses, even if granted.',
    examples: ['Yes — student visa 2018', 'No'],
  },
  'applicant.visa_history.previous_visa_refused_or_cancelled': {
    description:
      'Has any country (including Australia) ever refused you a visa or cancelled an existing visa? Be honest — this is checked.',
    examples: ['No', 'Yes — UK visitor visa refused 2017'],
  },
  'applicant.character.criminal_conviction': {
    description:
      'Have you ever been convicted of any offence anywhere in the world, including minor traffic offences in some cases? Disclose everything.',
    examples: ['No', 'Yes — speeding fine 2019'],
  },
  'applicant.character.outstanding_debts_au': {
    description:
      'Money currently owed to the Australian Government or a public authority — for example unpaid tax debts, Centrelink or Medicare overpayments, overdue HELP/HECS repayments, or an unpaid debt from a previous visa matter. Answer Yes if any amount is still outstanding.',
    examples: ['No', 'Yes — ATO payment plan, $2,400 remaining'],
  },
  'sponsor.character.outstanding_debts_au': {
    description:
      'Money currently owed to the Australian Government or a public authority — for example unpaid tax debts, Centrelink or Medicare overpayments, overdue HELP/HECS repayments, or an unpaid debt from a previous visa matter. Answer Yes if any amount is still outstanding.',
    examples: ['No', 'Yes — ATO payment plan, $2,400 remaining'],
  },
  'applicant.health.hap_id': {
    description:
      'Health Assessment Portal ID — given to you after starting your health examination on the Department of Home Affairs site.',
    examples: ['HAP1234567'],
  },

  // ---------------------------------------------------------------------------
  // Witnesses (Form 888 prep)
  // ---------------------------------------------------------------------------
  'applicant.witnesses.family_name': {
    description: "Witness's last name as on their Australian ID.",
    examples: ['Wilson', 'Patel'],
  },
  'applicant.witnesses.given_names': {
    description: "Witness's first and middle names as on their Australian ID.",
    examples: ['Jennifer Mary', 'Raj Kumar'],
  },
  'applicant.witnesses.email': {
    description:
      "Witness's email address. They will receive a Form 888 to complete and sign.",
    examples: ['jenny.wilson@outlook.com'],
  },
  'applicant.witnesses.phone_mobile': {
    description: "Witness's mobile number with country code.",
    examples: ['+61 412 345 678'],
  },
  'applicant.witnesses.relationship_to_applicant': {
    description:
      'How the witness knows you and your sponsor as a couple.',
    examples: ['Close friend of both', 'Workmate', 'Cousin of sponsor'],
  },
  'applicant.witnesses.years_known': {
    description:
      'How many years the witness has known you and/or your sponsor as a couple.',
    examples: ['5', '12'],
  },

  // ---------------------------------------------------------------------------
  // Application admin
  // ---------------------------------------------------------------------------
  'application.applicant_trn': {
    description:
      'Transaction Reference Number. Generated by ImmiAccount when an application is started — leave blank if you have not lodged yet.',
    examples: ['EGOABCD1234'],
  },
};

/**
 * Look up a tooltip for a field by its canonical_name.
 * Strips `[*]` wildcards so repeater entries match the same key as
 * a flat field with the same path.
 */
export function lookupFieldTooltip(canonicalName: string): FieldTooltipEntry | undefined {
  if (!canonicalName) return undefined;
  const cleaned = canonicalName.replace(/\[\*\]/g, '');
  return FIELD_TOOLTIPS[cleaned];
}
